import React, { useState, useEffect } from 'react'
import {
  TrendingUp,
  AlertCircle,
  Target,
  Users,
  DollarSign,
  BarChart3,
  RefreshCw,
  Sparkles,
  ArrowUpRight,
  Zap,
  Shield
} from 'lucide-react'
import axios from 'axios'

interface MarketTrend {
  category: string
  demand: number
  competition: number
  priceRange: string
  growth: number
  opportunities: string[]
  risks: string[]
}

// 市场卡片组件
const MarketCard: React.FC<{
  category: string
  demand: number
  competition: number
  growth: number
  priceRange: string
  icon: React.ReactNode
  color: string
}> = ({ category, demand, competition, growth, priceRange, icon, color }) => {
  // 计算得分等级
  const getDemandLevel = (d: number) =>
    d >= 80 ? { label: '旺盛', color: 'text-emerald-600 bg-emerald-50 border-emerald-200' } :
    d >= 60 ? { label: '稳定', color: 'text-blue-600 bg-blue-50 border-blue-200' } :
    { label: '一般', color: 'text-amber-600 bg-amber-50 border-amber-200' }

  const getCompLevel = (c: number) =>
    c >= 80 ? { label: '激烈', color: 'text-rose-600 bg-rose-50 border-rose-200' } :
    c >= 60 ? { label: '中等', color: 'text-amber-600 bg-amber-50 border-amber-200' } :
    { label: '较低', color: 'text-emerald-600 bg-emerald-50 border-emerald-200' }

  const demandLevel = getDemandLevel(demand)
  const compLevel = getCompLevel(competition)

  return (
    <div className="group bg-white rounded-2xl border border-slate-100 overflow-hidden hover:shadow-xl hover:shadow-slate-100/50 transition-all hover:-translate-y-1">
      {/* 顶部渐变 */}
      <div className={`h-1.5 bg-gradient-to-r ${color}`} />

      <div className="p-6">
        {/* 头部 */}
        <div className="flex items-center gap-3 mb-5">
          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform`}>
            {icon}
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900">{category}课程</h3>
            <span className="text-xs text-slate-500">{priceRange}</span>
          </div>
        </div>

        {/* 指标 */}
        <div className="space-y-4">
          {/* 需求指数 */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-600">需求指数</span>
              <span className={`px-2 py-0.5 rounded-lg text-xs font-bold border ${demandLevel.color}`}>
                {demandLevel.label}
              </span>
            </div>
            <div className="relative h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`absolute inset-y-0 left-0 bg-gradient-to-r ${color} rounded-full transition-all duration-500`}
                style={{ width: `${demand}%` }}
              />
            </div>
            <div className="text-right text-xs text-slate-500 mt-1">{demand}%</div>
          </div>

          {/* 竞争程度 */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-600">竞争程度</span>
              <span className={`px-2 py-0.5 rounded-lg text-xs font-bold border ${compLevel.color}`}>
                {compLevel.label}
              </span>
            </div>
            <div className="relative h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`absolute inset-y-0 left-0 ${
                  competition >= 80 ? 'bg-rose-400' : competition >= 60 ? 'bg-amber-400' : 'bg-emerald-400'
                } rounded-full transition-all duration-500`}
                style={{ width: `${competition}%` }}
              />
            </div>
            <div className="text-right text-xs text-slate-500 mt-1">{competition}%</div>
          </div>

          {/* 增长率 */}
          <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-xl border border-emerald-100">
            <div className="flex items-center gap-2 text-emerald-700">
              <TrendingUp className="w-4 h-4" />
              <span className="text-sm font-medium">增长率</span>
            </div>
            <span className="text-lg font-bold text-emerald-600">+{growth}%</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export const TeacherMarketAnalysis: React.FC = () => {
  const [marketData, setMarketData] = useState<MarketTrend[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchMarketAnalysis()
  }, [])

  const fetchMarketAnalysis = async () => {
    try {
      setLoading(true)
      const response = await axios.get('/api/teacher-ai/market-analysis')
      setMarketData(response.data.trends || [])
    } catch (err) {
      console.error('获取市场分析失败:', err)
      setError('获取市场分析数据失败，请稍后重试')
      setMarketData([
        {
          category: '数学',
          demand: 85,
          competition: 65,
          priceRange: '100-300元/课时',
          growth: 12,
          opportunities: ['小学数学思维培养需求增长', '奥数竞赛辅导市场扩大'],
          risks: ['竞争激烈', '价格透明化']
        },
        {
          category: '英语',
          demand: 90,
          competition: 80,
          priceRange: '120-400元/课时',
          growth: 8,
          opportunities: ['在线口语陪练需求高', '商务英语培训兴起'],
          risks: ['外教竞争', 'AI翻译工具普及']
        },
        {
          category: '编程',
          demand: 95,
          competition: 45,
          priceRange: '150-500元/课时',
          growth: 25,
          opportunities: ['青少年编程教育火热', '人工智能课程需求大'],
          risks: ['技术更新快', '免费资源多']
        }
      ])
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="relative mb-6">
          <div className="w-16 h-16 rounded-full border-4 border-emerald-100 border-t-emerald-500 animate-spin" />
          <BarChart3 className="w-6 h-6 text-emerald-500 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
        </div>
        <p className="text-slate-600 font-medium">AI 正在分析市场数据...</p>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* 头部 */}
      <div className="relative rounded-3xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtNi42MjcgMC0xMiA1LjM3My0xMiAxMnM1LjM3MyAxMiAxMiAxMiAxMi01LjM3MyAxMi0xMi01LjM3My0xMi0xMi0xMnptMCAxOGMtMy4zMTQgMC02LTIuNjg2LTYtNnMyLjY4Ni02IDYtNiA2IDIuNjg2IDYgNi0yLjY4NiA2LTYgNnoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjEiLz48L2c+PC9zdmc+')] opacity-20" />
        <div className="relative px-8 py-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-1">市场分析报告</h2>
                <p className="text-white/80">AI 分析教育市场趋势，发现商业机会</p>
              </div>
            </div>
            <button
              onClick={fetchMarketAnalysis}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-xl font-medium transition-all"
            >
              <RefreshCw className="w-4 h-4" />
              刷新数据
            </button>
          </div>
        </div>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <span className="text-sm text-amber-800">{error} (显示示例数据)</span>
        </div>
      )}

      {/* 市场概览统计 */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-slate-100 p-5 text-center">
          <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl flex items-center justify-center text-white mx-auto mb-3 shadow-lg">
            <Zap className="w-5 h-5" />
          </div>
          <div className="text-2xl font-bold text-slate-900">编程</div>
          <div className="text-sm text-slate-500">最热门领域</div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-5 text-center">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl flex items-center justify-center text-white mx-auto mb-3 shadow-lg">
            <TrendingUp className="w-5 h-5" />
          </div>
          <div className="text-2xl font-bold text-slate-900">+15%</div>
          <div className="text-sm text-slate-500">平均增长率</div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-5 text-center">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-500 rounded-xl flex items-center justify-center text-white mx-auto mb-3 shadow-lg">
            <DollarSign className="w-5 h-5" />
          </div>
          <div className="text-2xl font-bold text-slate-900">¥200+</div>
          <div className="text-sm text-slate-500">均价水平</div>
        </div>
      </div>

      {/* 分类市场分析卡片 */}
      <div className="grid gap-6 md:grid-cols-3">
        <MarketCard
          category="编程"
          demand={95}
          competition={45}
          growth={25}
          priceRange="150-500元/课时"
          icon={<TrendingUp className="w-6 h-6" />}
          color="from-emerald-400 to-teal-500"
        />
        <MarketCard
          category="英语"
          demand={90}
          competition={80}
          growth={8}
          priceRange="120-400元/课时"
          icon={<BarChart3 className="w-6 h-6" />}
          color="from-blue-400 to-indigo-500"
        />
        <MarketCard
          category="数学"
          demand={85}
          competition={65}
          growth={12}
          priceRange="100-300元/课时"
          icon={<Target className="w-6 h-6" />}
          color="from-purple-400 to-pink-500"
        />
      </div>

      {/* AI 洞察 */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* 机会 */}
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl border border-emerald-100 p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl flex items-center justify-center text-white shadow-lg">
              <Zap className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900">市场机会</h3>
              <p className="text-sm text-slate-500">AI 发现的增长点</p>
            </div>
          </div>
          <div className="space-y-3">
            {[
              '青少年编程教育市场持续火热',
              '在线口语陪练需求量大',
              '小学数学思维培养成为刚需',
              '人工智能课程需求爆发式增长'
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-emerald-100">
                <ArrowUpRight className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                <span className="text-sm text-slate-700">{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* 风险 */}
        <div className="bg-gradient-to-br from-rose-50 to-orange-50 rounded-3xl border border-rose-100 p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 bg-gradient-to-br from-rose-400 to-orange-500 rounded-xl flex items-center justify-center text-white shadow-lg">
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900">风险提示</h3>
              <p className="text-sm text-slate-500">需要注意的挑战</p>
            </div>
          </div>
          <div className="space-y-3">
            {[
              '英语类课程竞争激烈',
              '技术类课程更新迭代快',
              '免费学习资源增多',
              '价格透明化压缩利润空间'
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-rose-100">
                <AlertCircle className="w-4 h-4 text-rose-500 flex-shrink-0" />
                <span className="text-sm text-slate-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
